import { OnboardingStatus } from '@shared/models';

export class OnboardingConsts {
  public static readonly CompanySummary = 'CompanySummary';
  public static readonly CompanyOnboarding = 'CompanyOnboarding';
  public static readonly RequiredToStart = 'RequiredToStart';
  public static readonly HelpfulTemplates = 'HelpfulTemplates';
  public static readonly OptionalLater = 'OptionalLater';
  public static readonly InviteTeamMembers = 'InviteTeamMembers';
  public static readonly SetupTemplatesAndDefaults = 'SetupTemplatesAndDefaults';
  public static readonly CreateFirstProject = 'CreateFirstProject';
  public static readonly AddMaterials = 'AddMaterials';
  public static readonly AddMaterialPackages = 'AddMaterialPackages';
  public static readonly AddQaCheckItems = 'AddQaCheckItems';
  public static readonly AddCustomersCrm = 'AddCustomersCrm';
  public static readonly ConfigureTimeCostCategories = 'ConfigureTimeCostCategories';
  public static readonly StepDone = 'StepDone';
  public static readonly ViewDismissed = 'ViewDismissed';
  public static readonly RequiredToStartProgressPopup = 'RequiredToStartProgressPopup';
  public static readonly ShowCompanyOnboardingProgress = 'showCompanyOnboardingProgress';
  public static readonly ProjectOnboarding = 'ProjectOnboarding';
  public static readonly UploadPlansAndProjectFiles = 'UploadPlansAndProjectFiles';
  public static readonly ConfigureProject = 'ConfigureProject';
  public static readonly Materials = 'Materials';
  public static readonly PlanTheProject = 'PlanTheProject';
  public static readonly SetupQACheckMatrix = 'SetupQACheckMatrix';
  public static readonly ShowProjectOnboardingProgress = 'ShowProjectOnboardingProgress';
  public static readonly ProjectMasterOnboarding = 'ProjectMasterOnboarding';
}

export type OnboardingStepStatus = 'done' | 'inProgress' | 'notStarted';

export type CompanyOnboardingStepMetadata = {
  minutes: number;
  routerLink: string[];
  queryParams?: Record<string, string>;
  titleKey: string;
  descriptionKey: string;
};

export const COMPANY_ONBOARDING_STEP_STATUS_MAP: Record<OnboardingStatus, OnboardingStepStatus> = {
  [OnboardingStatus.Done]: 'done',
  [OnboardingStatus.InProgress]: 'inProgress',
  [OnboardingStatus.NotStarted]: 'notStarted',
};

export const COMPANY_ONBOARDING_STEP_METADATA: Record<string, CompanyOnboardingStepMetadata> = {
  [OnboardingConsts.InviteTeamMembers]: {
    minutes: 20,
    routerLink: ['/app', 'master-data', 'employees'],
    titleKey: 'dashboard.companyWelcome.setupCards.items.inviteTeamMembers.title',
    descriptionKey: 'dashboard.companyWelcome.setupCards.items.inviteTeamMembers.description',
  },
  [OnboardingConsts.SetupTemplatesAndDefaults]: {
    minutes: 5,
    routerLink: ['/app', 'master-data', 'templates-and-defaults'],
    titleKey: 'dashboard.companyWelcome.setupCards.items.setupTemplatesAndDefaults.title',
    descriptionKey: 'dashboard.companyWelcome.setupCards.items.setupTemplatesAndDefaults.description',
  },
  [OnboardingConsts.CreateFirstProject]: {
    minutes: 10,
    routerLink: ['/app', 'project', 'projects'],
    queryParams: { openCreate: 'true' },
    titleKey: 'dashboard.companyWelcome.setupCards.items.createFirstProject.title',
    descriptionKey: 'dashboard.companyWelcome.setupCards.items.createFirstProject.description',
  },
  [OnboardingConsts.AddMaterials]: {
    minutes: 15,
    routerLink: ['/app', 'master-data', 'materials'],
    titleKey: 'dashboard.companyWelcome.setupCards.items.addMaterials.title',
    descriptionKey: 'dashboard.companyWelcome.setupCards.items.addMaterials.description',
  },
  [OnboardingConsts.AddMaterialPackages]: {
    minutes: 10,
    routerLink: ['/app', 'master-data', 'material-packages'],
    titleKey: 'dashboard.companyWelcome.setupCards.items.addMaterialPackages.title',
    descriptionKey: 'dashboard.companyWelcome.setupCards.items.addMaterialPackages.description',
  },
  [OnboardingConsts.AddQaCheckItems]: {
    minutes: 10,
    routerLink: ['/app', 'master-data', 'quality-check-item-templates'],
    titleKey: 'dashboard.companyWelcome.setupCards.items.addQaCheckItems.title',
    descriptionKey: 'dashboard.companyWelcome.setupCards.items.addQaCheckItems.description',
  },
  [OnboardingConsts.AddCustomersCrm]: {
    minutes: 5,
    routerLink: ['/app', 'master-data', 'customers'],
    titleKey: 'dashboard.companyWelcome.setupCards.items.addCustomersCrm.title',
    descriptionKey: 'dashboard.companyWelcome.setupCards.items.addCustomersCrm.description',
  },
  [OnboardingConsts.ConfigureTimeCostCategories]: {
    minutes: 5,
    routerLink: ['/app', 'master-data', 'time-and-cost-categories'],
    titleKey: 'dashboard.companyWelcome.setupCards.items.configureTimeCostCategories.title',
    descriptionKey: 'dashboard.companyWelcome.setupCards.items.configureTimeCostCategories.description',
  },
};

export const DEFAULT_COMPANY_ONBOARDING_STEP_METADATA: CompanyOnboardingStepMetadata = {
  minutes: 0,
  routerLink: ['/app', 'dashboard'],
  titleKey: '',
  descriptionKey: '',
};

export type ProjectOnboardingStepMetadata = {
  minutes: number;
  routerLink: string[];
  icon: string;
  queryParams?: Record<string, string>;
  titleKey: string;
  descriptionKey: string;
};

export const PROJECT_ONBOARDING_STEP_STATUS_MAP: Record<OnboardingStatus, OnboardingStepStatus> = {
  [OnboardingStatus.Done]: 'done',
  [OnboardingStatus.InProgress]: 'inProgress',
  [OnboardingStatus.NotStarted]: 'notStarted',
};

export const PROJECT_ONBOARDING_STEP_METADATA: Record<string, ProjectOnboardingStepMetadata> = {
  [OnboardingConsts.UploadPlansAndProjectFiles]: {
    minutes: 5,
    routerLink: ['/app', 'project', ':projectId', 'files'],
    icon: 'pi pi-upload',
    titleKey: 'dashboard.projectOnboarding.items.uploadFiles.title',
    descriptionKey: 'dashboard.projectOnboarding.items.uploadFiles.description',
  },
  [OnboardingConsts.ConfigureProject]: {
    minutes: 10,
    routerLink: ['/app', 'project', ':projectId', 'setup'],
    icon: 'pi pi-sliders-h',
    titleKey: 'dashboard.projectOnboarding.items.configureProject.title',
    descriptionKey: 'dashboard.projectOnboarding.items.configureProject.description',
  },
  [OnboardingConsts.Materials]: {
    minutes: 15,
    routerLink: ['/app', 'project', ':projectId', 'material-assignment'],
    icon: 'pi pi-pencil',
    queryParams: { openCreate: 'true' },
    titleKey: 'dashboard.projectOnboarding.items.materials.title',
    descriptionKey: 'dashboard.projectOnboarding.items.materials.description',
  },
  [OnboardingConsts.PlanTheProject]: {
    minutes: 20,
    routerLink: ['/app', 'project', ':projectId', 'files'],
    icon: 'pi pi-map',
    queryParams: { folder: 'plans' },
    titleKey: 'dashboard.projectOnboarding.items.planProject.title',
    descriptionKey: 'dashboard.projectOnboarding.items.planProject.description',
  },
  [OnboardingConsts.SetupQACheckMatrix]: {
    minutes: 15,
    routerLink: ['/app', 'project', ':projectId', 'quality-check-items'],
    icon: 'pi pi-check-circle',
    titleKey: 'dashboard.projectOnboarding.items.qaCheckMatrix.title',
    descriptionKey: 'dashboard.projectOnboarding.items.qaCheckMatrix.description',
  },
};

export const DEFAULT_PROJECT_ONBOARDING_STEP_METADATA: ProjectOnboardingStepMetadata = {
  minutes: 0,
  routerLink: ['/app', 'project', ':projectId', 'dashboard'],
  icon: '',
  titleKey: '',
  descriptionKey: '',
};

export const ONBOARDING_MARKER_CLASSES: Record<OnboardingStepStatus, string> = {
  done: 'bg-green-500 text-white',
  inProgress: 'bg-blue-600 text-white',
  notStarted: 'border border-gray-300 text-gray-500 dark:border-gray-600 dark:text-gray-300',
};

export const ONBOARDING_ICON_CLASSES: Partial<Record<OnboardingStepStatus, string>> = {
  done: 'pi pi-check text-white',
  inProgress: 'pi pi-chevron-right',
};

export const ONBOARDING_CHIP_CLASSES: Record<OnboardingStepStatus, string> = {
  done: 'border-green-500 bg-green-100 text-green-700 dark:border-green-400 dark:bg-green-950 dark:text-green-300',
  inProgress: 'border-blue-500 bg-blue-100 text-blue-700 dark:border-blue-400 dark:bg-blue-950 dark:text-blue-300',
  notStarted: 'border-gray-200 bg-gray-100 text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

export const ONBOARDING_CHIP_ICON_CLASSES: Partial<Record<OnboardingStepStatus, string>> = {
  done: 'pi pi-check',
};

export const ONBOARDING_TITLE_CLASSES: Record<OnboardingStepStatus, string> = {
  done: 'text-gray-900 dark:text-white',
  inProgress: 'text-blue-600 dark:text-blue-300',
  notStarted: 'text-gray-900 dark:text-white',
};
